// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import { getDynamicLabelValues, renderLabel } from '@tcp/core/src/utils/labels/labelUtilty';
import styles, { BopisWrapper } from '../styles/BopisPromotionBanner.style';

const isValid = (value) => value > 0;

/**
 * @description Generates a promotional banner text by replacing placeholders with appropriate values.
 *
 * This function determines the most relevant promotional message to display based on the availability
 * of store specials and BOPIS promotions. It prioritizes store special promotions over BOPIS promotions
 * when applicable.
 *
 * @param {Object} params - The parameters for generating the promotional text.
 * @param {string} params.bannerBopisText - The default BOPIS promotional text containing the placeholder.
 * @param {string} params.BOPIS_PROMO_DISCOUNT - The discount value for BOPIS promotions.
 * @param {string} params.defaultBopisPromotionDiscountValue - A fallback discount value if BOPIS_PROMO_DISCOUNT is not available.
 * @param {Object} params.storeSpecialList - An object containing special store promotions.
 * @param {string} params.storeSpecialPrice - A specific price for a store special promotion.
 * @param {string} params.bannerSSText - The promotional text for store specials.
 * @param {string} params.bannerSSPriceText - The promotional text for store specials with a specific price.
 * @returns {string} - The promotional banner text with placeholders replaced by actual values.
 */
const promoBannerText = ({
  bannerBopisText,
  BOPIS_PROMO_DISCOUNT,
  defaultBopisPromotionDiscountValue,
  storeSpecialList,
  storeSpecialPrice,
  bannerSSText,
  bannerSSPriceText,
  isStoreSpecialsPdP,
}) => {
  const searchString = '#bopisPercentValue#';
  const storeList = Object.values(storeSpecialList);
  const valueToReplace = BOPIS_PROMO_DISCOUNT || defaultBopisPromotionDiscountValue;
  if (storeList.length && isStoreSpecialsPdP) {
    if (storeSpecialPrice) {
      if (storeSpecialPrice !== '0.00') {
        return bannerSSPriceText.replace(searchString, `$${storeSpecialPrice}`); // actual price.
      }
      return bannerBopisText.replace(searchString, valueToReplace); //
    }
    return bannerSSText.replace(searchString, Math.max(...storeList.map(Number))); // render on delivery methods.
  }
  return bannerBopisText.replace(searchString, valueToReplace); // bopis promotion
};
// eslint-disable-next-line sonarjs/cognitive-complexity, complexity
const BopisPromotionBanner = (props) => {
  const labels = useMemo(() => getDynamicLabelValues(`dynamic.browse_d`), []);
  const bannerBopisText = useMemo(
    () => renderLabel(labels.lbl_banner_bopis_text, 'lbl_banner_bopis_text'),
    [labels]
  );
  const bannerSSText = useMemo(
    () => renderLabel(labels.lbl_banner_store_special_text, 'lbl_banner_store_special_text'),
    [labels]
  );
  const bannerSSPriceText = useMemo(
    () =>
      renderLabel(
        labels.lbl_banner_store_special_price_text,
        'lbl_banner_store_special_price_text'
      ),
    [labels]
  );
  const [isBannerTextLoaded, setIsBannerTextLoaded] = useState(!isEmpty(bannerBopisText));
  const [bannerText, setBannerText] = useState('');

  const {
    className,
    isBopisEnabled,
    isBopisPromoDiscountBannerEnabled,
    defaultBopisPromotionDiscountValue,
    offerTypeBasedPromotion,
    isPickupMobilePromotion,
    isNewQVEnabled,
    storeSpecialList,
    storeSpecialPrice,
    isStoreSpecialsPdP,
  } = props;
  const { BOPIS_PROMO_DISCOUNT = 0 } = offerTypeBasedPromotion || {};
  let displayValue = null;

  if (isValid(BOPIS_PROMO_DISCOUNT)) {
    displayValue = BOPIS_PROMO_DISCOUNT;
  } else if (isValid(defaultBopisPromotionDiscountValue)) {
    displayValue = defaultBopisPromotionDiscountValue;
  } else if (!isEmpty(storeSpecialList)) {
    displayValue = true;
  }

  useEffect(() => {
    if (!isEmpty(bannerBopisText)) {
      setIsBannerTextLoaded(true);
      const bt = promoBannerText({
        bannerBopisText,
        BOPIS_PROMO_DISCOUNT,
        defaultBopisPromotionDiscountValue,
        bannerSSText,
        storeSpecialList,
        storeSpecialPrice,
        bannerSSPriceText,
        isStoreSpecialsPdP,
      });
      setBannerText(bt);
    }
  }, [bannerBopisText]);

  useEffect(() => {
    const bt = promoBannerText({
      bannerBopisText,
      BOPIS_PROMO_DISCOUNT,
      defaultBopisPromotionDiscountValue,
      bannerSSText,
      storeSpecialList,
      storeSpecialPrice,
      bannerSSPriceText,
      isStoreSpecialsPdP,
    });
    setBannerText(bt);
  }, [storeSpecialList, isStoreSpecialsPdP]);

  if (!isBannerTextLoaded) {
    return null;
  }
  const storeList = (!isEmpty(storeSpecialList) && Object.values(storeSpecialList)) || [];

  if (
    (!isBopisPromoDiscountBannerEnabled && !storeList.length) ||
    !isBopisEnabled ||
    !displayValue
  ) {
    return null;
  }
  const triangleClass = !isPickupMobilePromotion ? 'triangle-left' : 'triangle-top';

  return (isBopisPromoDiscountBannerEnabled || storeList.length) && bannerText ? (
    <BopisWrapper className={`${className} banner-wrapper`}>
      {!isNewQVEnabled ? <div className={triangleClass} /> : <></>}
      <div className="promo-wrapper">
        <div className="richtextCss">
          <RichText richTextHtml={bannerText} />
        </div>
      </div>
    </BopisWrapper>
  ) : (
    <></>
  );
};

BopisPromotionBanner.propTypes = {
  className: PropTypes.string.isRequired,
  isBopisEnabled: PropTypes.bool.isRequired,
  isBopisPromoDiscountBannerEnabled: PropTypes.bool.isRequired,
  defaultBopisPromotionDiscountValue: PropTypes.string.isRequired,
  offerTypeBasedPromotion: PropTypes.shape({}),
  isPickupMobilePromotion: false,
  isNewQVEnabled: PropTypes.shape({}),
};

BopisPromotionBanner.defaultProps = {
  offerTypeBasedPromotion: {},
  isPickupMobilePromotion: false,
  isNewQVEnabled: false,
};

export default withStyles(BopisPromotionBanner, styles);
export { BopisPromotionBanner as BopisPromotionBannerVanilla };
